import {
  IconName,
  type IconNameType,
  IconVariant,
  type IconVariantType,
  pathPattern,
  truthy,
  useIsFeatureEnabled,
  validateLength
} from "@quortex/front-common"
import type { MakeGenerics } from "@tanstack/react-location"
import { useSuspenseQuery } from "@tanstack/react-query"
import { usePool } from "src/queries/playPools"

export const statusToBadgeColor = {
  active: "success",
  configuring: "info",
  disabled: "grey",
  warning: "warning"
} as const

export const statusToFilterRadioType = {
  active: "success",
  configuring: "loading",
  disabled: "disabled",
  down: "error",
  warning: "warning"
} as const

export const statusToStatusColor = {
  active: "active",
  configuring: "configuring",
  disabled: "disabled",
  down: "error",
  warning: "warning"
} as const

const typeToFormat: Record<api.ott.InputStream["type"], string> = {
  rtmp: "RTMP",
  srt: "SRT",
  virtual: "Virtual"
}

/**
 * Return OTT endpoint options which have not been created yet
 */
export const getOttEndpointOptions = (
  endpoints: api.ott.OttEndpoint[],
  inputUuid: string,
  processings: api.ott.Processing[],
  targets: api.ott.Target[]
) =>
  processings
    .flatMap(p => targets.map(t => ({ processingUuid: p.uuid, targetUuid: t.uuid })))
    .filter(
      ({ processingUuid, targetUuid }) =>
        !endpoints.some(
          e =>
            e.inputUuid === inputUuid &&
            e.processingUuid === processingUuid &&
            e.targetUuid === targetUuid
        )
    )

export type BackGenerics = MakeGenerics<{
  Search: { back: Record<string, unknown> }
}>

export const useOttEndpointDomainName = (cdn?: api.ott.Cdn) => {
  const { data: pool } = useSuspenseQuery(usePool())
  const isCustomDomainEnabled = useIsFeatureEnabled("custom_domain")
  return isCustomDomainEnabled && cdn?.customDomain?.enabled
    ? cdn.customDomain?.domain
    : pool.domain
}

export const useOttEndpointUrl = (endpoint: api.ott.OttEndpoint, cdn?: api.ott.Cdn) => {
  const { data: pool } = useSuspenseQuery(usePool())
  const domainName = useOttEndpointDomainName(cdn)
  return `https://${domainName}${pool.pathPrefix ?? ""}/${endpoint.customPath || endpoint.pretty}`
}

export const getInputTags = (input?: api.ott.InputV2 | null) => {
  const tags: { label: string; iconType?: IconVariantType; icon?: IconNameType }[] = []

  if (!input) {
    return tags
  }

  const inputType = input.source === "ingest" ? input.ingest?.type : input.local?.type
  const inputConfig =
    input.source === "ingest"
      ? input.ingest?.[input.ingest?.type]
      : input.local?.[input.local?.type]

  return [
    input.source === "ingest" && {
      icon: IconName.INGEST,
      iconType: IconVariant.REGULAR,
      label: "Ingest"
    },
    inputType && { label: typeToFormat[inputType] },
    inputConfig && {
      icon: IconName.CODE_BRANCH,
      iconType: IconVariant.REGULAR,
      label: `${"streamRedundancy" in inputConfig && inputConfig.streamRedundancy === "dual" ? "Dual" : "Single"} stream`
    }
  ].filter(truthy)
}

export const validatePath = (targetType?: api.ott.Target["type"]) => (value?: string | null) => {
  if (!value) return true

  const validatePathFormat = (extension: string, errorMessage: string) => {
    if (value.endsWith(extension)) {
      return validateLength("path", 0, 100)(value)
    } else {
      return errorMessage
    }
  }

  if (pathPattern.test(value)) {
    switch (targetType) {
      case "dash":
        return validatePathFormat(
          ".mpd",
          "DASH OTT endpoints should end with .mpd (e.g. my_processing/my_target/index.mpd)"
        )
      case "hls":
        return validatePathFormat(
          ".m3u8",
          "HLS OTT endpoints should end with .m3u8 (e.g. my_processing/my_target/index.m3u8)"
        )
      default:
        return validateLength("path", 0, 100)(value)
    }
  }

  return "The OTT endpoint URL should be a valid (and relative) path"
}
